import { type LanguageCode, isLanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { translateApiErrors } from 'Services/api-error/api-error.service';
import { activityActions } from 'Store/entities/activity/activity.slice';
import { type BreakingNews as BreakingNewsType } from 'Store/entities/activity/activity.type';

import styles from './BreakingNews.style';
import { BreakingNewsFormValues, useSubmitBreakingNewsForm } from './BreakingNewsForm.hook';
import { ActionBar } from './components/ActionBar/ActionBar';
import { Audience } from './components/Tabs/Audience/Audience';
import { BreakingNewsContentForm } from './components/Tabs/Content/BreakingNewsContentForm';
import { withFetchBreakingNewsHoc } from './hoc/withBreakingNews';
import { inferInitialTabIndexFromPath } from './routing.utils';
import { SaveAudienceModal } from '../AudienceForm/SaveAudienceModal/SaveCampaignAudienceModal';
import { audienceFormatterFromStore } from '../AudienceForm/audience.utils';
import { parseNotificationTime, stringToDate } from './libs/dateTimeUtils';

export type BreakingNewsProps = {
  breakingNews: BreakingNewsType;
};

type InferFunctionArguments<T extends (...args: any[]) => any> = T extends (...args: infer U) => any ? U : never;

const TAB_MAPPING: Record<number, FC> = {
  0: BreakingNewsContentForm,
  1: Audience,
};

export const BreakingNews: FC<BreakingNewsProps> = withFetchBreakingNewsHoc(({ breakingNews }) => {
  const dispatch = useAppDispatch();

  const [visibleSaveModal, setVisibleSaveModal] = useState(false);
  const { selectedLanguage } = useTypedSelector((state) => state.entities.activity.edition);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const languageParam = urlParams.get('language');

    if (languageParam && isLanguageCode(languageParam)) {
      if (breakingNews.languages.includes(languageParam)) {
        dispatch(activityActions.setEditionLanguage(languageParam));
      }
    }
  }, [breakingNews.languages, dispatch]);

  const defaultValues = useMemo(
    () => ({
      name: breakingNews?.informations?.name || undefined,
      text: breakingNews?.informations?.text || undefined,
      description: breakingNews?.informations?.description || undefined,
      coverMedia: breakingNews?.informations?.coverMedia || undefined,
      isLongText: breakingNews?.informations?.isLongText || false,
      isRecurringNotification: breakingNews?.notification?.isRecurringNotification || false,
      isNotificationActive: breakingNews?.notification?.isNotificationActive || false,
      notificationTime: parseNotificationTime(breakingNews?.notification?.notificationTime || ''),
      startDate: breakingNews?.scheduling?.startDate ? stringToDate(breakingNews?.scheduling?.startDate) : undefined,
      endDate: breakingNews?.scheduling?.endDate ? stringToDate(breakingNews?.scheduling?.endDate) : undefined,
      audiences: audienceFormatterFromStore(breakingNews?.audiences),
      audienceDirty: false,
      audienceTypeChanged: false,
    }),
    [breakingNews],
  );

  const { setValue, watch, setError, clearErrors, reset, ...restOfFormMethods } = useForm<BreakingNewsFormValues>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const [currentTabIndex, setCurrentTabIndex] = useState(() => inferInitialTabIndexFromPath());

  const { handleSaveBreakingNews, onBreakingNewsValidation, handleBreakingNewsAudienceSave } =
    useSubmitBreakingNewsForm({
      breakingNews,
      watch,
      reset,
      defaultValues,
      setVisibleSaveModal,
      isDirty: restOfFormMethods.formState.isDirty,
    });

  const { error } = useTypedSelector((state) => state.entities.activity.edition);

  const tabs = useMemo(() => [t('activities:tabs.content'), t('activities:tabs.audience')], []);
  const Tab = TAB_MAPPING[currentTabIndex];

  const breakingNewsLastSave = useMemo(() => {
    if (!breakingNews.informations.lastSave) {
      return '';
    }

    return moment(breakingNews.informations.lastSave).calendar({
      sameDay: `[${t('activities:dates.same_day_save')}] LT`,
      lastDay: `[${t('activities:dates.last_day_save')}] LT`,
      lastWeek: `[${t('activities:dates.last_save_long_date')}] DD/MM/YYYY`,
      sameElse: `[${t('activities:dates.last_save_long_date')}] DD/MM/YYYY`,
    });
  }, [breakingNews.informations.lastSave]);

  useEffect(() => {
    if (error === null || typeof error === 'string') {
      return;
    }

    const informationsError = error?.[selectedLanguage]?.informations || error?.informations;

    if (typeof informationsError !== 'object' || Array.isArray(informationsError) || informationsError === null) {
      return;
    }

    const informationsErrorKeys = Object.keys(informationsError) as (keyof typeof informationsError)[];
    informationsErrorKeys.map((key) => {
      const errorMessage = informationsError[key];

      if (!errorMessage || !(key in defaultValues)) {
        return;
      }

      setError(key as keyof typeof defaultValues, {
        message: translateApiErrors({ [key]: errorMessage }),
      });
    });
  }, [error, selectedLanguage]);

  const handleLanguageChange = useCallback(
    (newSelectedLanguage: LanguageCode) => {
      dispatch(activityActions.setEditionLanguage(newSelectedLanguage));
    },
    [dispatch, activityActions],
  );

  return (
    <FormProvider
      {...restOfFormMethods}
      watch={watch}
      setValue={(...args: InferFunctionArguments<typeof setValue>) => {
        const [name, value, config] = args;
        setValue(name, value, {
          ...config,
          shouldDirty: true,
        });
      }}
      setError={setError}
      clearErrors={clearErrors}
      reset={reset}
    >
      <div style={styles.container}>
        <div style={styles.actionBar}>
          <ActionBar
            disableSave={!restOfFormMethods.formState.isDirty && !watch('audienceDirty')}
            tabs={tabs}
            currentTabIndex={currentTabIndex}
            breakingNewsId={breakingNews.id}
            breakingNewsStatus={breakingNews.status}
            breakingNewsName={watch(`name.${selectedLanguage}`) || ''}
            breakingNewsLastSave={breakingNewsLastSave}
            onTabChange={setCurrentTabIndex}
            breakingNewsLanguages={breakingNews.languages}
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
            onLiveToggle={onBreakingNewsValidation}
            onSave={handleSaveBreakingNews}
          />
        </div>
        <div style={styles.containerTabs}>
          <Tab />
        </div>
      </div>

      {visibleSaveModal && (
        <SaveAudienceModal
          scope="breaking-news"
          onAction={(validated) => {
            if (validated) {
              handleBreakingNewsAudienceSave(watch('audiences'), watch('audienceTypeChanged'), breakingNews.audiences);
            } else {
              setVisibleSaveModal(false);
            }
          }}
        />
      )}
    </FormProvider>
  );
});
